// ═════════════════════════════════════════════════════════════════════════════
// NHCX Pre-Authorization & Claim API client
//
// Contract: PREAUTH_CLAIM_UI_INTEGRATION.md (repo root).
//
//   POST /app/api/insurance/preauth/submit/        → 200 { correlation_id, poll_url, gateway_response, … }
//   POST /app/api/insurance/claim/submit/          → same envelope; body adds pre_auth_ref
//   GET  /app/api/insurance/response/{id}/         → 404 while waiting, 200 once the payer answers
//   POST /app/api/insurance/dummy-payer/trigger/   → sandbox only; the dummy payer is otherwise passive
//
// Both submits are ASYNCHRONOUS. A 200 means "submitted to NHCX", not "here is
// the decision". A 200 can still carry gateway_response.result.protocol_status
// other than "request.queued" — that is an NHCX rejection, not a success.
// ═════════════════════════════════════════════════════════════════════════════

import axios from "axios";
import {
  NHCX_BASE_URL,
  nhcxUrl,
  nhcxHeaders,
  pollNhcxResponse,
  normalizeNhcxError,
} from "./nhcx-client";

export { pollNhcxResponse, NHCX_BASE_URL };

export function normalizeSubmitError(err) {
  return normalizeNhcxError(err, {
    404: "Admission not found on the NHCX service.",
    500: "The NHCX service failed to build the request. Quote the API call id to support.",
  });
}

/** POST pre-auth. Resolves with the submission envelope (correlation_id inside). */
export async function submitPreauth(body, correlationId) {
  const url = correlationId
    ? `${nhcxUrl("/preauth/submit/")}?correlation_id=${encodeURIComponent(correlationId)}`
    : nhcxUrl("/preauth/submit/");
  const res = await axios.post(url, body, { headers: nhcxHeaders() });
  return res.data;
}

/** POST claim. Identical body to pre-auth, plus mandatory pre_auth_ref. */
export async function submitClaim(body, correlationId) {
  const url = correlationId
    ? `${nhcxUrl("/claim/submit/")}?correlation_id=${encodeURIComponent(correlationId)}`
    : nhcxUrl("/claim/submit/");
  const res = await axios.post(url, body, { headers: nhcxHeaders() });
  return res.data;
}

/**
 * Sandbox dummy payer is passive — nothing happens after submit until this is
 * called. Wait ~10 seconds first or NHCX returns SBX-004 (request not queued yet).
 *
 * action: "Approve" | "Reject" | "Query"
 * method: "Preauth" | "Claim"
 */
export async function triggerDummyPayer({ action, method, correlationId }) {
  const res = await axios.post(
    nhcxUrl("/dummy-payer/trigger/"),
    { action, method, correlation_id: correlationId },
    { headers: nhcxHeaders() }
  );
  return res.data;
}

/** protocol_status "request.queued" means NHCX accepted it. Anything else is a rejection. */
export function readGatewayStatus(envelope) {
  const result = envelope?.gateway_response?.result;
  const error = envelope?.gateway_response?.error;
  const protocolStatus = result?.protocol_status || "";
  return {
    queued: protocolStatus === "request.queued",
    protocolStatus,
    error,
  };
}
